/**
 * Tutorial Mode — global toggle for guidance (hover) tooltips.
 *
 * When Tutorial Mode is OFF, every tooltip rendered through the central
 * `@/components/Tooltip` entry point is hidden UNLESS it is explicitly marked
 * `essential` (delete/irreversible-action warnings, payment amount/settlement
 * info). Default is ON, so existing behaviour is unchanged (opt-out).
 *
 * Storage/event pattern mirrors sidebarTriggerMode.ts. The reactive React
 * binding lives in `@/contexts/TutorialModeContext` (a provider + hook), so the
 * value is read via a single context subscription instead of a per-instance
 * window listener — `Tooltip` renders hundreds of times on heavy tables.
 */
export type TutorialMode = 'on' | 'off';

const STORAGE_KEY = 'ggc_tutorial_mode';

export const TUTORIAL_MODE_EVENT = 'ggc:tutorial-mode-change';

export const DEFAULT_TUTORIAL_MODE: TutorialMode = 'on';

export function getTutorialMode(): TutorialMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'on' || stored === 'off') return stored;
  } catch { /* ignore */ }
  return DEFAULT_TUTORIAL_MODE;
}

export function setTutorialMode(mode: TutorialMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch { /* ignore */ }
  window.dispatchEvent(
    new CustomEvent<TutorialMode>(TUTORIAL_MODE_EVENT, { detail: mode }),
  );
}
